import { useState, useRef, useEffect } from "react";
import { PRIORITY_COLOR, STAGE_CFG } from "./constants";

// ═══════════════════════════════════════════════════════════
// STYLE HELPERS
// ═══════════════════════════════════════════════════════════
export const S = {
  row:  (gap = 8, wrap = false) => ({ display: "flex", alignItems: "center", gap, flexWrap: wrap ? "wrap" : "nowrap" }),
  col:  (gap = 8)               => ({ display: "flex", flexDirection: "column", gap }),
  inputBase: {
    width: "100%", padding: "7px 10px", border: "1.5px solid #e4e4ed",
    borderRadius: "8px", fontSize: "12.5px", color: "#1a1a2e", outline: "none",
    background: "#fafafa", transition: "border-color .15s, box-shadow .15s",
  },
  selectBase: {
    width: "100%", padding: "7px 28px 7px 10px", border: "1.5px solid #e4e4ed",
    borderRadius: "8px", fontSize: "12.5px", color: "#1a1a2e", outline: "none",
    background: "#fafafa", appearance: "none", cursor: "pointer",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%239e9eb8' stroke-width='1.2' stroke-linecap='round' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 9px center",
    transition: "border-color .15s",
  },
  label: {
    display: "block", fontSize: "11px", fontWeight: "600", color: "#8888a8",
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px",
  },
  sectionTitle: {
    fontSize: "10.5px", fontWeight: "700", color: "#a0a0c0",
    textTransform: "uppercase", letterSpacing: "0.7px",
    paddingBottom: "8px", borderBottom: "1px solid #f0f0f8", marginBottom: "14px",
  },
};

export const focusIn  = e => { e.target.style.borderColor = "#0f3460"; e.target.style.boxShadow = "0 0 0 3px rgba(15,52,96,.08)"; };
export const focusOut = e => { e.target.style.borderColor = "#e4e4ed"; e.target.style.boxShadow = "none"; };
export const fmt      = n => n != null && !isNaN(n) ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (n ? String(n) : "—");

export const useClickOutside = (ref, cb) => {
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) cb(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// ═══════════════════════════════════════════════════════════
// ICON LIBRARY
// ═══════════════════════════════════════════════════════════
const ICONS = {
  kanban:     <svg width="15" height="12" viewBox="0 0 15 12" fill="none"><rect x="0" y="0" width="4" height="12" rx="1.4" fill="currentColor"/><rect x="5.5" y="0" width="4" height="8.5" rx="1.4" fill="currentColor" opacity=".7"/><rect x="11" y="0" width="4" height="5" rx="1.4" fill="currentColor" opacity=".5"/></svg>,
  list:       <svg width="14" height="11" viewBox="0 0 14 11" fill="none"><rect x="0" y="0" width="14" height="2.5" rx="1.2" fill="currentColor"/><rect x="0" y="4.2" width="14" height="2.5" rx="1.2" fill="currentColor"/><rect x="0" y="8.4" width="14" height="2.5" rx="1.2" fill="currentColor"/></svg>,
  search:     <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M9 9l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
  plus:       <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  close:      <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  chevDown:   <svg width="8" height="5" viewBox="0 0 8 5" fill="none"><path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
  settings:   <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="2.3" stroke="currentColor" strokeWidth="1.3"/><path d="M7 1.2v1.5M7 11.3v1.5M1.2 7h1.5M11.3 7h1.5M2.9 2.9l1.1 1.1M10 10l1.1 1.1M10 2.9l-1.1 1.1M3 10.1l-1.1 1.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
  more:       <svg width="14" height="4" viewBox="0 0 14 4" fill="none"><circle cx="2" cy="2" r="1.5" fill="currentColor"/><circle cx="7" cy="2" r="1.5" fill="currentColor"/><circle cx="12" cy="2" r="1.5" fill="currentColor"/></svg>,
  moreV:      <svg width="4" height="14" viewBox="0 0 4 14" fill="none"><circle cx="2" cy="2" r="1.5" fill="currentColor"/><circle cx="2" cy="7" r="1.5" fill="currentColor"/><circle cx="2" cy="12" r="1.5" fill="currentColor"/></svg>,
  edit:       <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 9.5L8.5 3a1.7 1.7 0 012.4 2.4L4.4 12H2V9.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>,
  trash:      <svg width="12" height="13" viewBox="0 0 12 13" fill="none"><path d="M1 3h10M5 3V2h2v1M3 3l.6 9h4.8L9 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  filter:     <svg width="13" height="10" viewBox="0 0 13 10" fill="none"><path d="M1 1h11M3 5h7M5 9h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  bookmark:   <svg width="10" height="13" viewBox="0 0 10 13" fill="none"><path d="M1 1h8v11L5 9l-4 3V1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>,
  phone:      <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 2.3C2 8.1 5 11 10.7 11l.5-2.3-2.3-.5-1.4 1.5C6 9 4 7 3.3 5.5l1.5-1.4-.5-2.3-2.3.5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  mail:       <svg width="13" height="10" viewBox="0 0 13 10" fill="none"><rect x="1" y="1" width="11" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.2"/><path d="M1 2.5l5.5 3.5L12 2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  link:       <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M5 8l3-3M7.5 3H10V5.5M10 3L5.5 7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 4H4a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
  copy:       <svg width="12" height="13" viewBox="0 0 12 13" fill="none"><rect x="3.5" y="3.5" width="7.5" height="8.5" rx="1.2" stroke="currentColor" strokeWidth="1.2"/><path d="M1 9V2a1 1 0 011-1h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  note:       <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1.5" y="1.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2"/><path d="M4 5h5M4 7h5M4 9h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  activity:   <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.2"/><path d="M6.5 3.5V6.5l2 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  check:      <svg width="11" height="8"  viewBox="0 0 11 8"  fill="none"><path d="M1 4l3 3 6-6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  arrow:      <svg width="14" height="8"  viewBox="0 0 14 8"  fill="none"><path d="M1 4h12M8.5 1L12 4l-3.5 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  collapse:   <svg width="7" height="12" viewBox="0 0 7 12"  fill="none"><path d="M6 1L1 6l5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  expand:     <svg width="7" height="12" viewBox="0 0 7 12"  fill="none"><path d="M1 1l5 5-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  user:       <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.2"/><path d="M1 12c0-3 2.5-4.5 5.5-4.5S12 9 12 12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  dollar:     <svg width="11" height="13" viewBox="0 0 11 13" fill="none"><path d="M5.5 1v11M2 4c0-1.5 1.5-2.5 3.5-2.5S9 2.5 9 4s-1.5 2.5-3.5 2.5S2 7.5 2 9s1.5 2.5 3.5 2.5S9 10.5 9 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  calendar:   <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1" y="2" width="11" height="10" rx="1.2" stroke="currentColor" strokeWidth="1.2"/><path d="M4 1v2.5M9 1v2.5M1 5.5h11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  pipeline:   <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1" y="1" width="3.5" height="11" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="5.5" y="1" width="3.5" height="7.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="10" y="1" width="2" height="4.5" rx="1" stroke="currentColor" strokeWidth="1.2"/></svg>,
  import:     <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1v8M3.5 6l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M1 11h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  automation: <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1l1.1 2.9 3 .4-2.2 2.2.5 3-2.4-1.3L4 9.5l.5-3L2.3 4.3l3-.4L6.5 1z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/></svg>,
  info:       <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.2"/><path d="M6.5 6v3.5M6.5 4.2V5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>,
  archive:    <svg width="13" height="12" viewBox="0 0 13 12" fill="none"><rect x="1" y="1" width="11" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M2 4v6a1 1 0 001 1h7a1 1 0 001-1V4" stroke="currentColor" strokeWidth="1.2"/><path d="M4.5 7h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  restore:    <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5a4.5 4.5 0 104.5-4.5H5M5 2L3 4l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  whatsapp:   <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1a5.5 5.5 0 100 11 5.5 5.5 0 000-11zm0 0v.01M4 9l.5-1.5A3 3 0 114 9z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
};

export const Ic = ({ n, s = 13, c = "currentColor" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", color: c, flexShrink: 0, width: s, height: s }}>
    {ICONS[n] || null}
  </span>
);

// ═══════════════════════════════════════════════════════════
// ATOM COMPONENTS
// ═══════════════════════════════════════════════════════════
export const Avatar = ({ user, size = 26, showDot = false, title: t }) => {
  // Support both "avatar" and "avatar_url" field names from different data sources
  const imgSrc = user?.avatar || user?.avatar_url || null;
  const initials = user?.initials
    || (user?.name ? user.name.split(" ").filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2) : "?");
  return (
    <div title={t || user?.name} style={{
      width: size, height: size, borderRadius: "50%",
      background: imgSrc ? "transparent" : (user?.color || "#757575"),
      flexShrink: 0, position: "relative", display: "flex", alignItems: "center",
      justifyContent: "center", color: "#fff", fontSize: size * 0.38, fontWeight: "700",
      overflow: "hidden",
    }}>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={user?.name || ""}
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "block" }}
          onError={e => {
            // If image fails, hide it and show initials fallback
            e.target.style.display = "none";
            e.target.parentNode.style.background = user?.color || "#757575";
          }}
        />
      ) : (
        <span style={{ lineHeight: 1 }}>{initials}</span>
      )}
      {showDot && (
        <div style={{
          position: "absolute", bottom: 0, right: 0,
          width: size * 0.34, height: size * 0.34, borderRadius: "50%",
          background: user?.status === "online" ? "#4CAF50" : user?.status === "away" ? "#FF9800" : "#9E9E9E",
          border: "1.5px solid #fff",
          zIndex: 2,
        }} />
      )}
    </div>
  );
};

export const StagePill = ({ stage, sm }) => {
  const c = STAGE_CFG[stage] || { bg: "#f0f0f5", text: "#555" };
  return (
    <span style={{
      padding: sm ? "2px 7px" : "3px 10px", borderRadius: "20px",
      background: c.bg, color: c.text,
      fontSize: sm ? "10.5px" : "11.5px", fontWeight: "700", whiteSpace: "nowrap",
    }}>
      {stage}
    </span>
  );
};

export const TagChip = ({ tag, sm, onRemove }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: "3px",
    padding: sm ? "2px 6px" : "3px 9px", borderRadius: "12px",
    background: tag.color + "18", color: tag.color,
    border: `1px solid ${tag.color}30`,
    fontSize: sm ? "10px" : "11.5px", fontWeight: "500", whiteSpace: "nowrap",
  }}>
    {tag.label}
    {onRemove && (
      <button onClick={onRemove} style={{ border: "none", background: "none", cursor: "pointer", color: tag.color, lineHeight: 1, padding: 0, fontSize: "10px", marginLeft: "1px" }}>
        ✕
      </button>
    )}
  </span>
);

export const PriorityDot = ({ p, size = 7 }) => (
  <span style={{ width: size, height: size, borderRadius: "50%", background: PRIORITY_COLOR[p] || "#ccc", display: "inline-block", flexShrink: 0 }} />
);

export const ProbBar = ({ value, height = 3 }) => (
  <div style={{ height, background: "#f0f0f8", borderRadius: "2px", overflow: "hidden" }}>
    <div style={{
      height: "100%", width: `${value || 0}%`,
      background: value >= 70 ? "#43A047" : value >= 40 ? "#FB8C00" : "#E53935",
      borderRadius: "2px", transition: "width .3s",
    }} />
  </div>
);

// ═══════════════════════════════════════════════════════════
// FORM PRIMITIVES
// ═══════════════════════════════════════════════════════════
export const Field = ({ label, children, required }) => (
  <div style={{ marginBottom: "14px" }}>
    <label style={S.label}>{label}{required && <span style={{ color: "#E53935", marginLeft: "2px" }}>*</span>}</label>
    {children}
  </div>
);

export const Input = ({ value, onChange, placeholder, type = "text", style: extra, ...rest }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder}
    style={{ ...S.inputBase, ...extra }} onFocus={focusIn} onBlur={focusOut} {...rest} />
);

export const Sel = ({ value, onChange, children, style: extra, ...rest }) => (
  <select value={value} onChange={onChange} style={{ ...S.selectBase, ...extra }} onFocus={focusIn} onBlur={focusOut} {...rest}>
    {children}
  </select>
);

export const Textarea = ({ value, onChange, placeholder, rows = 4 }) => (
  <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
    style={{ ...S.inputBase, resize: "vertical", minHeight: "70px" }}
    onFocus={focusIn} onBlur={focusOut} />
);

// ═══════════════════════════════════════════════════════════
// DROPDOWN MENU
// ═══════════════════════════════════════════════════════════
export function Dropdown({ trigger, items, right = false, top = "calc(100% + 5px)" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useClickOutside(ref, () => setOpen(false));
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <div onClick={() => setOpen(o => !o)}>{trigger}</div>
      {open && (
        <div style={{
          position: "absolute", top, right: right ? "0" : undefined, left: right ? undefined : "0",
          background: "#fff", borderRadius: "10px", boxShadow: "0 6px 24px rgba(0,0,0,0.13)",
          border: "1px solid #ecedf2", minWidth: "182px", zIndex: 9999,
          overflow: "hidden", animation: "fadeIn .12s ease",
        }}>
          {items.map((item, i) =>
            item === "---"
              ? <div key={i} style={{ height: "1px", background: "#f0f0f8", margin: "3px 0" }} />
              : <div key={i}
                  onClick={() => { item.onClick && item.onClick(); setOpen(false); }}
                  style={{ padding: "9px 14px", fontSize: "12.5px", color: item.danger ? "#E53935" : "#2a2a4a", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", transition: "background .1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = item.danger ? "#fff5f5" : "#f5f5ff"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {item.icon && <Ic n={item.icon} s={13} c={item.danger ? "#E53935" : "#6e6e80"} />}
                  {item.label}
                </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ICON BUTTON
// ═══════════════════════════════════════════════════════════
export const IconBtn = ({ icon, onClick, title, size = 30, color = "#8888a8", bg = "#fff", border = "1.5px solid #e0e0e8" }) => (
  <button onClick={onClick} title={title} style={{
    width: size, height: size, border, borderRadius: "7px", background: bg,
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color,
    transition: "all .15s",
  }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = "#0f3460"; e.currentTarget.style.color = "#0f3460"; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = border === "none" ? "none" : "#e0e0e8"; e.currentTarget.style.color = color; }}>
    <Ic n={icon} s={Math.round(size * 0.45)} />
  </button>
);
